import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { fmtMoney } from '../../utils/formatters'

var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function InsightsPage() {
  var { activeBusiness } = useAuth()
  var [biz, setBiz] = useState(activeBusiness !== 'all' ? activeBusiness : 'all')
  var [range, setRange] = useState('year')
  var [bids, setBids] = useState([])
  var [jobs, setJobs] = useState([])
  var [expenses, setExpenses] = useState([])
  var [timeEntries, setTimeEntries] = useState([])
  var [loading, setLoading] = useState(true)
  var [schedRows, setSchedRows] = useState([])
  var [dayOffReqs, setDayOffReqs] = useState([])
  var [scheduleWorkers, setScheduleWorkers] = useState([])
  var [segments, setSegments] = useState([])

  var fetchData = useCallback(async function() {
    setLoading(true)
    var now = new Date()
    var startDate = range === 'month' ? new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
      : range === 'quarter' ? new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1).toISOString().split('T')[0]
      : range === 'year' ? now.getFullYear() + '-01-01'
      : '2020-01-01'

    var bQ = supabase.from('bids').select('*').gte('created_at', startDate)
    var jQ = supabase.from('jobs').select('*').gte('created_at', startDate)
    var eQ = supabase.from('expenses').select('*').gte('expense_date', startDate)
    var tQ = supabase.from('job_time_entries').select('*, profiles(full_name), jobs(job_type, pool_dimensions, estimated_hours)').not('clock_out', 'is', null).gte('clock_in', startDate)
    if (biz !== 'all') { bQ = bQ.eq('business_id', biz); jQ = jQ.eq('business_id', biz); eQ = eQ.eq('business_id', biz) }

    // Scheduling metrics: last 4 weeks
    var fourWeeksAgo = new Date(now.getTime() - 28 * 86400000).toISOString().slice(0, 10)
    var sQ = supabase.from('worker_schedule').select('*').gte('schedule_date', fourWeeksAgo)
    var dQ = supabase.from('day_off_requests').select('*').gte('created_at', fourWeeksAgo)
    var wQ = supabase.from('profiles').select('id, full_name, role, availability_status')
      .neq('role', 'store_partner')
      .eq('availability_status', 'available')

    var [bRes, jRes, eRes, tRes, sRes, dRes, wRes] = await Promise.all([bQ, jQ, eQ, tQ, sQ, dQ, wQ])
    setBids(bRes.data || [])
    setJobs(jRes.data || [])
    setExpenses(eRes.data || [])
    setTimeEntries(tRes.data || [])
    setSchedRows(sRes.data || [])
    setDayOffReqs(dRes.data || [])
    setScheduleWorkers(wRes.data || [])
    // Segments only matter for Riverbend; fetch segments for jobs in the range.
    var segmentedJobs = (jRes.data || []).filter(function(j) { return j.has_segments })
    if (segmentedJobs.length > 0) {
      var { data: segData } = await supabase.from('job_segments').select('*').in('job_id', segmentedJobs.map(function(j) { return j.id }))
      setSegments(segData || [])
    } else {
      setSegments([])
    }
    setLoading(false)
  }, [biz, range])

  useEffect(function() { fetchData() }, [fetchData])

  if (loading) return <div style={st.loading}>Loading insights...</div>

  // === COMPUTED METRICS ===
  var submitted = bids.filter(function(b) { return b.status === 'submitted' })
  var awarded = bids.filter(function(b) { return b.status === 'awarded' || b.status === 'completed' })
  var lost = bids.filter(function(b) { return b.status === 'lost' })
  var decided = awarded.length + lost.length
  var winRate = decided > 0 ? Math.round((awarded.length / decided) * 100) : 0
  var sum = function(arr) { return arr.reduce(function(s, b) { return s + (parseFloat(b.total) || 0) }, 0) }
  var awardedVal = sum(awarded)
  var lostVal = sum(lost)
  var avgContract = awarded.length > 0 ? awardedVal / awarded.length : 0
  var largestWon = awarded.length > 0 ? Math.max.apply(null, awarded.map(function(b) { return parseFloat(b.total) || 0 })) : 0

  // Quote funnel
  var quoteSent = bids.filter(function(b) { return b.quote_status }).length
  var quoteViewed = bids.filter(function(b) { return b.quote_status === 'viewed' || b.quote_status === 'signed' }).length
  var quoteSigned = bids.filter(function(b) { return b.quote_status === 'signed' }).length

  // Follow-up needed
  var needsFollowUp = submitted.filter(function(b) { return b.updated_at && (Date.now() - new Date(b.updated_at).getTime()) > 7 * 86400000 }).length

  // Jobs metrics
  var completedJobs = jobs.filter(function(j) { return j.status === 'completed' })
  var scheduledJobs = jobs.filter(function(j) { return j.status === 'scheduled' })
  var cancelledJobs = jobs.filter(function(j) { return j.status === 'cancelled' })
  var totalRevenue = jobs.filter(function(j) { return j.status === 'completed' || j.status === 'awarded' }).reduce(function(s, j) { return s + (parseFloat(j.contract_value) || 0) }, 0)

  // Monthly revenue
  var monthlyRev = Array(12).fill(0)
  jobs.filter(function(j) { return (j.status === 'completed' || j.status === 'awarded') && j.start_date }).forEach(function(j) {
    var m = parseInt(j.start_date.split('-')[1]) - 1
    if (m >= 0 && m < 12) monthlyRev[m] += (parseFloat(j.contract_value) || 0)
  })
  var maxMonthRev = Math.max.apply(null, monthlyRev.concat([1]))

  // Expenses
  var totalExpenses = expenses.reduce(function(s, e) { return s + (parseFloat(e.amount) || 0) }, 0)
  var grossProfit = totalRevenue - totalExpenses
  var profitMargin = totalRevenue > 0 ? Math.round((grossProfit / totalRevenue) * 100) : 0
  var catTotals = {}
  expenses.forEach(function(e) { catTotals[e.category || 'misc'] = (catTotals[e.category || 'misc'] || 0) + (parseFloat(e.amount) || 0) })
  var topCats = Object.keys(catTotals).sort(function(a, b) { return catTotals[b] - catTotals[a] }).slice(0, 8)

  // Scheduling metrics (last 4 weeks)
  var sched = schedRows.filter(function(r) { return r.status === 'scheduled' })
  var schedByUser = {}
  sched.forEach(function(r) { schedByUser[r.user_id] = (schedByUser[r.user_id] || 0) + 1 })
  var schedEntries = Object.keys(schedByUser).map(function(uid) {
    var w = scheduleWorkers.find(function(x) { return x.id === uid })
    return { id: uid, name: w?.full_name || 'Unknown', role: w?.role || '', days: schedByUser[uid] }
  }).sort(function(a, b) { return b.days - a.days })
  var avgDaysPerWorker = scheduleWorkers.length > 0 ? (sched.length / scheduleWorkers.length / 4).toFixed(1) : '0.0'
  var mostSched = schedEntries[0]
  var leastSched = schedEntries.length > 0 ? schedEntries[schedEntries.length - 1] : null
  var dayOffTotal = dayOffReqs.length
  var dayOffApproved = dayOffReqs.filter(function(r) { return r.status === 'approved' }).length
  var dayOffDenied = dayOffReqs.filter(function(r) { return r.status === 'denied' }).length
  var dayOffPending = dayOffReqs.filter(function(r) { return r.status === 'pending' }).length
  var dayOffRate = scheduleWorkers.length > 0 ? (dayOffTotal / scheduleWorkers.length / 4).toFixed(2) : '0.00'
  // Flag workers significantly below avg
  var flaggedLow = null
  if (schedEntries.length > 2) {
    var avg = sched.length / schedEntries.length
    var lowEntry = schedEntries[schedEntries.length - 1]
    if (lowEntry && lowEntry.days < avg * 0.5 && lowEntry.role === 'laborer') flaggedLow = lowEntry
  }

  // Riverbend multi-visit metrics (counts each segment as a visit)
  var segByJob = {}
  segments.forEach(function(s) { (segByJob[s.job_id] = segByJob[s.job_id] || []).push(s) })
  var segJobIds = Object.keys(segByJob)
  var totalVisits = segments.length
  var avgVisits = segJobIds.length > 0 ? (totalVisits / segJobIds.length).toFixed(1) : '0.0'
  var totalVisitDays = 0
  var maxGapDays = 0
  var totalGapDays = 0
  var jobsWithGap = 0
  segJobIds.forEach(function(jid) {
    var segs = segByJob[jid].slice().sort(function(a, b) { return (a.segment_number || 0) - (b.segment_number || 0) })
    segs.forEach(function(s) {
      var start = new Date(s.start_date + 'T12:00:00')
      var end = new Date((s.end_date || s.start_date) + 'T12:00:00')
      totalVisitDays += Math.round((end - start) / 86400000) + 1
    })
    var jobMax = 0
    for (var i = 1; i < segs.length; i++) {
      var prevEnd = new Date((segs[i - 1].end_date || segs[i - 1].start_date) + 'T12:00:00')
      var curStart = new Date(segs[i].start_date + 'T12:00:00')
      var gap = Math.round((curStart - prevEnd) / 86400000) - 1
      if (gap > 0) {
        totalGapDays += gap
        if (gap > jobMax) jobMax = gap
      }
    }
    if (jobMax > 0) jobsWithGap++
    if (jobMax > maxGapDays) maxGapDays = jobMax
  })
  var showVisitsCard = (biz === 'all' || biz === 'riverbend') && segJobIds.length > 0

  // Crew performance
  var crewStats = {}
  timeEntries.forEach(function(e) {
    var name = e.profiles?.full_name || 'Unknown'
    if (!crewStats[name]) crewStats[name] = { hours: 0, jobs: 0, onTime: 0 }
    crewStats[name].hours += (e.hours_worked || 0)
    crewStats[name].jobs++
    if (e.jobs?.estimated_hours && e.hours_worked <= e.jobs.estimated_hours) crewStats[name].onTime++
  })

  return (
    <div style={st.container}>
      <div style={st.header}>
        <div><div style={st.title}>INSIGHTS</div><div style={st.sub}>Business analytics and performance</div></div>
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
          <div style={{display:'flex',gap:'2px'}}>
            {['all','pools','riverbend'].map(function(b) { return <button key={b} onClick={function(){setBiz(b)}} style={{...st.filterBtn,...(biz===b?st.filterActive:{})}}>{b==='all'?'ALL':b==='pools'?'CPP':'RBC'}</button> })}
          </div>
          <div style={{display:'flex',gap:'2px'}}>
            {['month','quarter','year','all'].map(function(r) { return <button key={r} onClick={function(){setRange(r)}} style={{...st.filterBtn,...(range===r?st.filterActive:{})}}>{r.toUpperCase()}</button> })}
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div style={st.kpiRow}>
        <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a6a8c'}}>{bids.length}</div><div style={st.kpiLabel}>TOTAL BIDS</div><div style={st.kpiSub}>{fmtMoney(sum(bids))}</div></div>
        <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a8c6a'}}>{awarded.length}</div><div style={st.kpiLabel}>AWARDED</div><div style={st.kpiSub}>{fmtMoney(awardedVal)}</div></div>
        <div style={st.kpi}><div style={{...st.kpiVal,color:winRate>=50?'#4a8c6a':winRate>=25?'#b48c3c':'#e05c5c'}}>{winRate}%</div><div style={st.kpiLabel}>WIN RATE</div></div>
        <div style={st.kpi}><div style={{...st.kpiVal,color:'#e05c5c'}}>{lost.length}</div><div style={st.kpiLabel}>LOST</div><div style={st.kpiSub}>{fmtMoney(lostVal)}</div></div>
        <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{fmtMoney(avgContract)}</div><div style={st.kpiLabel}>AVG CONTRACT</div></div>
        <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{fmtMoney(largestWon)}</div><div style={st.kpiLabel}>LARGEST WON</div></div>
      </div>

      {/* Riverbend multi-visit metrics */}
      {showVisitsCard && (
        <div style={st.card}>
          <div style={st.cardTitle}>MULTI-VISIT JOBS (RIVERBEND)</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'12px'}}>
            <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a8c6a'}}>{segJobIds.length}</div><div style={st.kpiLabel}>MULTI-VISIT JOBS</div></div>
            <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a8c6a'}}>{totalVisits}</div><div style={st.kpiLabel}>TOTAL VISITS</div></div>
            <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{avgVisits}</div><div style={st.kpiLabel}>AVG VISITS / JOB</div></div>
            <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{totalVisitDays}</div><div style={st.kpiLabel}>TOTAL VISIT DAYS</div></div>
            <div style={st.kpi}><div style={{...st.kpiVal,color:maxGapDays>=14?'#e05c5c':'#4a6a8c'}}>{maxGapDays}</div><div style={st.kpiLabel}>LONGEST GAP (DAYS)</div></div>
            <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a6a8c'}}>{jobsWithGap}</div><div style={st.kpiLabel}>JOBS WITH GAPS</div><div style={st.kpiSub}>{totalGapDays} total gap days</div></div>
          </div>
        </div>
      )}

      {/* Scheduling metrics (last 4 weeks) */}
      <div style={st.card}>
        <div style={st.cardTitle}>SCHEDULING (LAST 4 WEEKS)</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'12px',marginBottom:'12px'}}>
          <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{avgDaysPerWorker}</div><div style={st.kpiLabel}>AVG DAYS/WORKER/WK</div></div>
          <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a8c6a',fontSize:'16px'}}>{mostSched ? mostSched.name : '—'}</div><div style={st.kpiLabel}>MOST SCHEDULED</div><div style={st.kpiSub}>{mostSched ? mostSched.days + ' days' : ''}</div></div>
          <div style={st.kpi}><div style={{...st.kpiVal,color:flaggedLow?'#e05c5c':'#b48c3c',fontSize:'16px'}}>{leastSched ? leastSched.name : '—'}</div><div style={st.kpiLabel}>LEAST SCHEDULED</div><div style={st.kpiSub}>{leastSched ? leastSched.days + ' days' : ''}{flaggedLow ? ' (flagged)' : ''}</div></div>
          <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a6a8c'}}>{dayOffTotal}</div><div style={st.kpiLabel}>DAY-OFF REQUESTS</div><div style={st.kpiSub}>{dayOffApproved} ok · {dayOffDenied} no · {dayOffPending} open</div></div>
          <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{dayOffRate}</div><div style={st.kpiLabel}>REQ/WORKER/WK</div></div>
        </div>
      </div>

      {/* Revenue Collected — completed jobs with amount_collected */}
      {(function() {
        var collectedJobs = jobs.filter(function(j) { return j.status === 'completed' && j.amount_collected != null })
        if (collectedJobs.length === 0) return null
        var totalCollected = collectedJobs.reduce(function(s, j) { return s + (parseFloat(j.amount_collected) || 0) }, 0)
        var byBiz = { pools: { total: 0, count: 0 }, riverbend: { total: 0, count: 0 } }
        var byMethod = {}
        var bidById = {}
        ;(bids || []).forEach(function(b) { bidById[b.id] = b })
        var bidSum = 0, collectedForVarianceSum = 0
        var byCustomer = {}
        collectedJobs.forEach(function(j) {
          var amt = parseFloat(j.amount_collected) || 0
          if (j.business_id && byBiz[j.business_id]) { byBiz[j.business_id].total += amt; byBiz[j.business_id].count += 1 }
          var method = j.payment_method || 'Unspecified'
          if (!byMethod[method]) byMethod[method] = { total: 0, count: 0 }
          byMethod[method].total += amt
          byMethod[method].count += 1
          if (j.bid_id && bidById[j.bid_id] && bidById[j.bid_id].total != null) {
            bidSum += parseFloat(bidById[j.bid_id].total) || 0
            collectedForVarianceSum += amt
          }
          var cName = j.client_name || 'Unknown'
          if (!byCustomer[cName]) byCustomer[cName] = 0
          byCustomer[cName] += amt
        })
        var variance = collectedForVarianceSum - bidSum
        var avgCollected = totalCollected / collectedJobs.length
        var topCustomers = Object.keys(byCustomer).map(function(k) { return { name: k, total: byCustomer[k] } }).sort(function(a, b) { return b.total - a.total }).slice(0, 5)

        // Weekly chart — last 8 weeks
        var weekTotalIn = function(start, end) {
          return collectedJobs.reduce(function(s, j) {
            if (!j.completed_at) return s
            var d = new Date(j.completed_at)
            return d >= start && d < end ? s + (parseFloat(j.amount_collected) || 0) : s
          }, 0)
        }
        var weeks = []
        var now = new Date()
        for (var w = 7; w >= 0; w--) {
          var weekStart = new Date(now.getTime() - w * 7 * 86400000)
          weekStart.setHours(0, 0, 0, 0)
          weekStart.setDate(weekStart.getDate() - weekStart.getDay())
          var weekEnd = new Date(weekStart.getTime() + 7 * 86400000)
          weeks.push({ start: weekStart, total: weekTotalIn(weekStart, weekEnd) })
        }
        var maxWeek = Math.max.apply(null, weeks.map(function(w) { return w.total }).concat([1]))

        // Month-over-month
        var thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
        var lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        var thisMonthTotal = collectedJobs.reduce(function(s, j) {
          if (!j.completed_at) return s
          var d = new Date(j.completed_at)
          return d >= thisMonthStart ? s + (parseFloat(j.amount_collected) || 0) : s
        }, 0)
        var lastMonthTotal = collectedJobs.reduce(function(s, j) {
          if (!j.completed_at) return s
          var d = new Date(j.completed_at)
          return d >= lastMonthStart && d < thisMonthStart ? s + (parseFloat(j.amount_collected) || 0) : s
        }, 0)
        var momChange = lastMonthTotal > 0 ? Math.round(((thisMonthTotal - lastMonthTotal) / lastMonthTotal) * 100) : null

        return (
          <div style={st.card}>
            <div style={st.cardTitle}>{String.fromCodePoint(0x1F4B0)} REVENUE COLLECTED</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:'12px',marginBottom:'14px'}}>
              <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a8c6a'}}>{fmtMoney(totalCollected)}</div><div style={st.kpiLabel}>TOTAL COLLECTED</div><div style={st.kpiSub}>{collectedJobs.length} jobs</div></div>
              <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{fmtMoney(avgCollected)}</div><div style={st.kpiLabel}>AVG / JOB</div></div>
              <div style={st.kpi}><div style={{...st.kpiVal,color:'#b48c3c'}}>{fmtMoney(byBiz.pools.total)}</div><div style={st.kpiLabel}>POOLS</div><div style={st.kpiSub}>{byBiz.pools.count} jobs</div></div>
              <div style={st.kpi}><div style={{...st.kpiVal,color:'#4a8c6a'}}>{fmtMoney(byBiz.riverbend.total)}</div><div style={st.kpiLabel}>RIVERBEND</div><div style={st.kpiSub}>{byBiz.riverbend.count} jobs</div></div>
              <div style={st.kpi}><div style={{...st.kpiVal,color:variance >= 0 ? '#4a8c6a' : '#e05c5c'}}>{(variance >= 0 ? '+' : '') + fmtMoney(variance)}</div><div style={st.kpiLabel}>VARIANCE VS BID</div><div style={st.kpiSub}>{collectedJobs.filter(function(j) { return j.bid_id }).length} bid-linked jobs</div></div>
              <div style={st.kpi}><div style={{...st.kpiVal,color:momChange === null ? 'rgba(255,255,255,0.5)' : (momChange >= 0 ? '#4a8c6a' : '#e05c5c')}}>{momChange === null ? '—' : (momChange >= 0 ? '+' : '') + momChange + '%'}</div><div style={st.kpiLabel}>VS LAST MONTH</div><div style={st.kpiSub}>{fmtMoney(thisMonthTotal)} / {fmtMoney(lastMonthTotal)}</div></div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'16px'}}>
              <div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',letterSpacing:'1.5px',color:'rgba(255,255,255,0.4)',marginBottom:'8px'}}>WEEKLY (LAST 8 WEEKS)</div>
                <div style={{display:'flex',alignItems:'flex-end',gap:'4px',height:'120px'}}>
                  {weeks.map(function(w, i) {
                    var pct = maxWeek > 0 ? (w.total / maxWeek) * 100 : 0
                    return (
                      <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'4px'}}>
                        <div style={{flex:1,width:'100%',display:'flex',alignItems:'flex-end'}}>
                          <div style={{width:'100%',backgroundColor:'#4a8c6a',height:pct+'%',borderRadius:'2px 2px 0 0',transition:'height 0.3s'}} title={fmtMoney(w.total)} />
                        </div>
                        <div style={{fontSize:'9px',color:'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif"}}>{(w.start.getMonth() + 1) + '/' + w.start.getDate()}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',letterSpacing:'1.5px',color:'rgba(255,255,255,0.4)',marginBottom:'8px'}}>BY PAYMENT METHOD</div>
                {Object.keys(byMethod).sort(function(a, b) { return byMethod[b].total - byMethod[a].total }).map(function(m) {
                  var pct = totalCollected > 0 ? Math.round((byMethod[m].total / totalCollected) * 100) : 0
                  return (
                    <div key={m} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 0',fontSize:'12px'}}>
                      <span style={{color:'rgba(255,255,255,0.7)'}}>{m}</span>
                      <span style={{color:'#b48c3c',fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600}}>{fmtMoney(byMethod[m].total)} <span style={{color:'rgba(255,255,255,0.35)',fontSize:'10px'}}>({pct}%)</span></span>
                    </div>
                  )
                })}
              </div>
            </div>

            {topCustomers.length > 0 && (
              <div style={{marginTop:'14px',paddingTop:'14px',borderTop:'1px solid rgba(255,255,255,0.06)'}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',letterSpacing:'1.5px',color:'rgba(255,255,255,0.4)',marginBottom:'8px'}}>TOP CUSTOMERS BY REVENUE</div>
                {topCustomers.map(function(c, i) {
                  return (
                    <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:'13px'}}>
                      <span style={{color:'rgba(255,255,255,0.75)'}}>{i + 1}. {c.name}</span>
                      <span style={{color:'#4a8c6a',fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600}}>{fmtMoney(c.total)}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })()}

      {/* Revenue & Jobs Row */}
      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'16px',marginBottom:'24px'}}>
        {/* Monthly Revenue Chart */}
        <div style={st.card}>
          <div style={st.cardTitle}>MONTHLY REVENUE</div>
          <div style={{display:'flex',alignItems:'flex-end',gap:'4px',height:'160px',padding:'12px 0'}}>
            {monthlyRev.map(function(val, i) {
              var pct = maxMonthRev > 0 ? (val / maxMonthRev) * 100 : 0
              return <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'4px'}}>
                <div style={{fontSize:'9px',color:'rgba(255,255,255,0.3)'}}>{val > 0 ? fmtMoney(val) : ''}</div>
                <div style={{width:'100%',backgroundColor:val>0?'#4a8c6a':'rgba(255,255,255,0.04)',borderRadius:'2px 2px 0 0',height:Math.max(pct, 2)+'%',minHeight:'2px'}} />
                <div style={{fontSize:'9px',color:'rgba(255,255,255,0.25)'}}>{MONTHS[i]}</div>
              </div>
            })}
          </div>
        </div>

        {/* Revenue Stats */}
        <div style={st.card}>
          <div style={st.cardTitle}>REVENUE & PROFIT</div>
          <div style={{display:'flex',flexDirection:'column',gap:'12px',padding:'8px 0'}}>
            <div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>TOTAL REVENUE</div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'24px',fontWeight:700,color:'#4a8c6a'}}>{fmtMoney(totalRevenue)}</div></div>
            <div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>TOTAL EXPENSES</div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'24px',fontWeight:700,color:'#e05c5c'}}>{fmtMoney(totalExpenses)}</div></div>
            <div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>GROSS PROFIT</div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'24px',fontWeight:700,color:grossProfit>=0?'#4a8c6a':'#e05c5c'}}>{fmtMoney(grossProfit)}</div></div>
            <div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>MARGIN</div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'20px',fontWeight:700,color:profitMargin>=20?'#4a8c6a':profitMargin>=10?'#b48c3c':'#e05c5c'}}>{profitMargin}%</div></div>
          </div>
        </div>
      </div>

      {/* Jobs & Pipeline Row */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'16px',marginBottom:'24px'}}>
        <div style={st.card}>
          <div style={st.cardTitle}>JOBS</div>
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Completed</span><span style={{color:'#4a8c6a',fontWeight:600}}>{completedJobs.length}</span></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Scheduled</span><span style={{color:'#4a6a8c',fontWeight:600}}>{scheduledJobs.length}</span></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Cancelled</span><span style={{color:'#e05c5c',fontWeight:600}}>{cancelledJobs.length}</span></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Total</span><span style={{color:'#ffffff',fontWeight:700}}>{jobs.length}</span></div>
          </div>
        </div>

        <div style={st.card}>
          <div style={st.cardTitle}>QUOTE FUNNEL</div>
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Sent</span><span style={{color:'#b48c3c',fontWeight:600}}>{quoteSent}</span></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Viewed</span><span style={{color:'#4a6a8c',fontWeight:600}}>{quoteViewed}</span></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Signed</span><span style={{color:'#4a8c6a',fontWeight:600}}>{quoteSigned}</span></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'rgba(255,255,255,0.4)',fontSize:'13px'}}>Needs Follow-up</span><span style={{color:'#e05c5c',fontWeight:600}}>{needsFollowUp}</span></div>
          </div>
        </div>

        <div style={st.card}>
          <div style={st.cardTitle}>PENDING</div>
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            <div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'28px',fontWeight:700,color:'#b48c3c'}}>{submitted.length}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>BIDS AWAITING</div></div>
            <div><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'20px',fontWeight:700,color:'rgba(255,255,255,0.6)'}}>{fmtMoney(sum(submitted))}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>PIPELINE VALUE</div></div>
          </div>
        </div>
      </div>

      {/* Expenses by Category */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'24px'}}>
        <div style={st.card}>
          <div style={st.cardTitle}>EXPENSES BY CATEGORY</div>
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            {topCats.map(function(cat) {
              var pct = totalExpenses > 0 ? (catTotals[cat] / totalExpenses) * 100 : 0
              return <div key={cat}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'12px',marginBottom:'3px'}}><span style={{color:'rgba(255,255,255,0.5)',textTransform:'uppercase'}}>{cat.replace(/_/g,' ')}</span><span style={{color:'#ffffff',fontWeight:600}}>{fmtMoney(catTotals[cat])}</span></div>
                <div style={{height:'6px',backgroundColor:'rgba(255,255,255,0.06)',borderRadius:'3px'}}><div style={{height:'100%',backgroundColor:'#b48c3c',borderRadius:'3px',width:pct+'%'}} /></div>
              </div>
            })}
          </div>
        </div>

        {/* Crew Performance */}
        <div style={st.card}>
          <div style={st.cardTitle}>CREW PERFORMANCE</div>
          {Object.keys(crewStats).length === 0 ? <div style={{color:'rgba(255,255,255,0.2)',fontSize:'13px',padding:'20px 0',textAlign:'center'}}>No time data yet</div> : (
            <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
              {Object.keys(crewStats).sort(function(a,b) { return crewStats[b].jobs - crewStats[a].jobs }).map(function(name) {
                var c = crewStats[name]
                var onTimePct = c.jobs > 0 ? Math.round((c.onTime / c.jobs) * 100) : 0
                return <div key={name} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                  <div><div style={{fontSize:'14px',fontWeight:600,color:'#ffffff'}}>{name}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)'}}>{c.jobs} jobs | {c.hours.toFixed(1)} hrs</div></div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'16px',fontWeight:700,color:onTimePct>=80?'#4a8c6a':onTimePct>=50?'#b48c3c':'#e05c5c'}}>{onTimePct}% on-time</div>
                </div>
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

var st = {
  container: { fontFamily: "'Barlow', sans-serif", maxWidth: '1100px' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '15px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  sub: { fontSize: '14px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  filterBtn: { padding: '7px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  filterActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' },
  kpi: { padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', textAlign: 'center' },
  kpiVal: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, lineHeight: 1 },
  kpiLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' },
  kpiSub: { fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '2px' },
  card: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px' },
  cardTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '2px', color: '#b48c3c', marginBottom: '16px' },
}
